import { publicConfig } from "@/configs/public-config";
import { TokenService } from "@/providers/tokens/tokens.service";
import {
  parseApiError,
  readErrorResponseBody,
  getFilenameFromContentDisposition as getFilenameFromContentDispositionShared,
  buildFetchError,
} from "@/shared/helpers/api-file-download.helper";
import { type IApiResponseError } from "@/shared/interfaces/api-response.interface";

import type { IExportCustomLayerBoundariesZipArg } from "./custom-layers.interface";

export interface IFetchCustomLayerBoundariesZipResult {
  data?: { blob: Blob; filename: string };
  error?: IApiResponseError;
}

const CUSTOM_LAYER_BOUNDARIES_ZIP_FALLBACK = "custom_layer_boundaries.zip";

export async function fetchCustomLayerBoundariesZip(
  arg: IExportCustomLayerBoundariesZipArg,
): Promise<IFetchCustomLayerBoundariesZipResult> {
  const { districtId, layerId } = arg;
  const baseUrl = `${publicConfig.apiUrl}/${publicConfig.apiTag}`;
  const url = `${baseUrl}/district/${districtId}/custom-layers/${layerId}/export/zip`;
  const accessToken = TokenService.getAccessToken();
  const headers: HeadersInit = {};

  if (accessToken) {
    headers[publicConfig.authHeader] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      const errorBody = await readErrorResponseBody(response);
      return { error: parseApiError(response, errorBody) };
    }

    const arrayBuffer = await response.arrayBuffer();
    const contentType = response.headers.get("Content-Type") ?? "application/zip";
    const blob = new Blob([arrayBuffer], { type: contentType });
    const filename = getFilenameFromContentDispositionShared(
      response.headers.get("Content-Disposition"),
      CUSTOM_LAYER_BOUNDARIES_ZIP_FALLBACK,
    );

    return { data: { blob, filename } };
  } catch (err) {
    return { error: buildFetchError(url, err) };
  }
}
